import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {PhoneNumber, PhoneNumberUtil} from 'google-libphonenumber';
import {ValidationService} from '../../../services/validation/validation.service';
import {debounceTime, ReplaySubject, Subject, Subscription} from 'rxjs';

export interface Region {
  region: string;
  code: number;
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
})
export class PhoneInputComponent implements OnInit, OnDestroy {
  regionCodes: Region[];
  regionCodesFiltered: ReplaySubject<Region[]> = new ReplaySubject<Region[]>();
  phoneNumberUtil: PhoneNumberUtil;
  _phoneNr: string;
  isNumberValid = true;
  isLengthValid = true;
  public PHONE_NO_MAX_LENGTH = 17;
  private _debounceSub: Subscription;
  private _phoneDebounce: Subject<string> = new Subject<string>();

  @Input() disabled: boolean;
  @Input() label: string;
  @Input() layout: string;
  @Input()
  set phoneNr(val: string) {
    this.updatePhoneNr(val);
    this._phoneNr = val;
    this.phoneNrChange.emit(this._phoneNr);
  }
  get phoneNr() {
    return this._phoneNr;
  }
  @Output() phoneNrChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  _number: string;
  set number(val: string) {
    this._number = val;
    this.emitPhoneNrChange();
  }
  get number() {
    return this._number;
  }

  _countryCode: number;
  set countryCode(val: number) {
    this._countryCode = val;
    this.emitPhoneNrChange();
  }
  get countryCode() {
    return this._countryCode;
  }

  convert(values: Region[]): Region[] {
    return values.map(region => {
      if (typeof region.code == 'string'){
        region.code = parseFloat(region.code);
      }
      return region;
    });
  }

  constructor(public validationService: ValidationService) {
    this.phoneNumberUtil = PhoneNumberUtil.getInstance();
  }

  public ngOnInit(): void {
    this.regionCodes = this.phoneNumberUtil.getSupportedRegions().map(v => {
      return { region: v, code: this.phoneNumberUtil.getCountryCodeForRegion(v) };
    });
    this._debounceSub = this._phoneDebounce.pipe(debounceTime(500)).subscribe((number: string) => {
      this.validate(number);
    });
  }
  public ngOnDestroy(): void {
    if (this._debounceSub) {
      this._debounceSub.unsubscribe();
      delete this._debounceSub;
    }
  }

  private updatePhoneNr(phoneNr: string): void {
    if (phoneNr) {
      try {
        const phoneNumber: PhoneNumber = this.phoneNumberUtil.parse(phoneNr);
        this._countryCode = phoneNumber.getCountryCode();
        this._number = phoneNumber.getNationalNumber().toString();
      } catch (e) {
        if ((e as Error).message === "The string supplied is too long to be a phone number") {
          const originalNr: string = phoneNr;
          const phoneNumber: PhoneNumber = this.phoneNumberUtil.parse(phoneNr.slice(0, this.PHONE_NO_MAX_LENGTH));
          this._countryCode = phoneNumber.getCountryCode();
          this._number = originalNr.slice(this._countryCode.toString().length + 1);
        } else if ((e as Error).message === "Invalid country calling code" || this.validationService.emptyOrNumeric(phoneNr, true)) {
          this._number = phoneNr;
        }
      }
    }
    this._phoneDebounce.next(this._number);
  }

  private emitPhoneNrChange(): void {
    if (this._number) {
      this.phoneNr = this._countryCode ? ('+' + this._countryCode + this._number) : this._number;
    } else {
      this.phoneNr = '';
    }
  }

  public validate(number: string): void {
    this.isLengthValid = number.length <= this.PHONE_NO_MAX_LENGTH;
    const isValid = this.validationService.emptyOrNumeric(number, true) && this.isLengthValid;
    if (this.isNumberValid !== isValid) {
      this.isValid.emit(isValid);
      this.isNumberValid = isValid;
    }
  }
}
